import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { Box, Typography, Chip, TextField, Button, useTheme } from '@mui/material';
import { tokens } from '../../theme';
import CoordinateGrid from './CoordinateGrid';
import GridControls from './GridControls';
import useCoordinateGrid from './useCoordinateGrid';
import { defaultCoordinateConfig, CURVE_POINTS_REQUIRED, formatElementSummary, formatCoordinate, getPolynomialPointsRequired, isCurveMode, getCurvePointsRequired } from './coordinateUtils';

/**
 * Student Component for Answering Coordinate Grid Questions
 * Interactive grid where students can place their answers
 */
const CoordinateGridStudent = ({
  config = defaultCoordinateConfig,
  value = { elements: [] },
  onChange,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const gridConfig = config.grid || defaultCoordinateConfig.grid;
  const allowedModes = useMemo(() => config.allowedModes || ['point'], [config.allowedModes]);
  const allowedPointInputMethods = useMemo(() => config.allowedPointInputMethods || ['click', 'type'], [config.allowedPointInputMethods]);
  const allowedLineInputMethods = useMemo(() => config.allowedLineInputMethods || ['twoPoints', 'equation'], [config.allowedLineInputMethods]);
  const allowedCurveInputMethods = useMemo(() => config.allowedCurveInputMethods || ['click', 'type'], [config.allowedCurveInputMethods]);

  const gridHook = useCoordinateGrid(value, config);
  const {
    elements,
    mode,
    setMode,
    pendingPoints,
    canUndo,
    undo,
    clear,
    handleGridClick,
    cancelOperation,
    getValue,
    setValue,
    addEquationLine,
    addTypedPoint,
    addTypedCurvePoint,
    curveOptionsRef,
  } = gridHook;

  // State for line input mode (twoPoints or equation)
  const [lineInputMode, setLineInputMode] = useState(allowedLineInputMethods[0] || 'twoPoints');
  const [equationInput, setEquationInput] = useState('');
  const [equationError, setEquationError] = useState('');

  // State for point input mode (click or type)
  const [pointInputMode, setPointInputMode] = useState(allowedPointInputMethods[0] || 'click');
  const [coordinateInput, setCoordinateInput] = useState('');
  const [coordinateError, setCoordinateError] = useState('');

  // State for curve input mode (click or type)
  const [curveInputMode, setCurveInputMode] = useState(allowedCurveInputMethods[0] || 'click');
  const [curveCoordinateInput, setCurveCoordinateInput] = useState('');
  const [curveCoordinateError, setCurveCoordinateError] = useState('');

  // State for logarithmic base selection
  const [logBase, setLogBase] = useState(10);
  const [customLogBase, setCustomLogBase] = useState('');

  // Sync logBase to hook's curveOptionsRef
  useEffect(() => {
    if (curveOptionsRef) {
      curveOptionsRef.current = { logBase };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logBase]);

  // Sync external value with internal state when value changes from outside
  useEffect(() => {
    if (JSON.stringify(value?.elements) !== JSON.stringify(elements)) {
      setValue(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Notify parent when elements change
  useEffect(() => {
    const currentValue = getValue();
    if (JSON.stringify(currentValue.elements) !== JSON.stringify(value?.elements)) {
      onChange?.(currentValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elements]);

  // Set initial mode to first allowed mode
  useEffect(() => {
    if (allowedModes.length > 0 && !allowedModes.includes(mode)) {
      setMode(allowedModes[0]);
    }
  }, [allowedModes, mode, setMode]);

  // Sync point input mode when allowed methods change
  useEffect(() => {
    if (!allowedPointInputMethods.includes(pointInputMode)) {
      setPointInputMode(allowedPointInputMethods[0] || 'click');
    }
  }, [allowedPointInputMethods, pointInputMode]);

  // Sync line input mode when allowed methods change
  useEffect(() => {
    if (!allowedLineInputMethods.includes(lineInputMode)) {
      setLineInputMode(allowedLineInputMethods[0] || 'twoPoints');
    }
  }, [allowedLineInputMethods, lineInputMode]);

  // Sync curve input mode when allowed methods change
  useEffect(() => {
    if (!allowedCurveInputMethods.includes(curveInputMode)) {
      setCurveInputMode(allowedCurveInputMethods[0] || 'click');
    }
  }, [allowedCurveInputMethods, curveInputMode]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        cancelOperation();
      } else if (e.key === 'z' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        undo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [cancelOperation, undo]);

  // Handle adding equation line
  const handleAddEquationLine = useCallback(() => {
    const gridBounds = {
      xMin: gridConfig.xMin,
      xMax: gridConfig.xMax,
      yMin: gridConfig.yMin,
      yMax: gridConfig.yMax,
    };
    const result = addEquationLine(equationInput, gridBounds);
    if (result?.error) {
      setEquationError(result.error);
    } else {
      setEquationInput('');
      setEquationError('');
    }
  }, [addEquationLine, equationInput, gridConfig]);

  // Handle adding typed point
  const handleAddTypedPoint = useCallback(() => {
    const gridBounds = {
      xMin: gridConfig.xMin,
      xMax: gridConfig.xMax,
      yMin: gridConfig.yMin,
      yMax: gridConfig.yMax,
    };
    const result = addTypedPoint(coordinateInput, gridBounds);
    if (result?.error) {
      setCoordinateError(result.error);
    } else {
      setCoordinateInput('');
      setCoordinateError('');
    }
  }, [addTypedPoint, coordinateInput, gridConfig]);

  // Handle adding typed curve point
  const handleAddTypedCurvePoint = useCallback(() => {
    const gridBounds = {
      xMin: gridConfig.xMin,
      xMax: gridConfig.xMax,
      yMin: gridConfig.yMin,
      yMax: gridConfig.yMax,
    };
    const result = addTypedCurvePoint(curveCoordinateInput, gridBounds);
    if (result?.error) {
      setCurveCoordinateError(result.error);
    } else {
      setCurveCoordinateInput('');
      setCurveCoordinateError('');
    }
  }, [addTypedCurvePoint, curveCoordinateInput, gridConfig]);

  return (
    <Box sx={{ width: '100%', maxWidth: 450 }}>
      {/* Instructions */}
      <Typography
        variant="body1"
        sx={{
          color: colors.blueAccent[400],
          fontWeight: 500,
          mb: 1.5,
          px: 1.5,
          py: 1,
          display: 'block',
          borderLeft: `3px solid ${colors.blueAccent[400]}`,
          backgroundColor: colors.primary[600],
          borderRadius: '0 4px 4px 0',
        }}
      >
        {pendingPoints.length > 0 ? (
          <span style={{ color: colors.greenAccent[400] }}>
            {mode === 'line' && 'Click to set the second point of the line'}
            {mode === 'polygon' &&
              `${pendingPoints.length} point${pendingPoints.length !== 1 ? 's' : ''} added. Click near the first point to close the polygon`}
            {mode === 'circle' && 'Click to set the radius of the circle'}
            {mode === 'quadratic' &&
              `${pendingPoints.length}/${CURVE_POINTS_REQUIRED.quadratic} points. ${curveInputMode === 'type' ? 'Type' : 'Click'} ${CURVE_POINTS_REQUIRED.quadratic - pendingPoints.length} more to define the parabola`}
            {mode === 'absolute' &&
              `${pendingPoints.length}/${CURVE_POINTS_REQUIRED.absolute} points. ${pendingPoints.length === 0 ? `${curveInputMode === 'type' ? 'Type' : 'Click'} the vertex first` : `${curveInputMode === 'type' ? 'Type' : 'Click'} one more point`}`}
            {mode === 'sqrt' &&
              `${pendingPoints.length}/${CURVE_POINTS_REQUIRED.sqrt} points. ${pendingPoints.length === 0 ? `${curveInputMode === 'type' ? 'Type' : 'Click'} the start point first` : `${curveInputMode === 'type' ? 'Type' : 'Click'} one more point`}`}
            {mode === 'nthRoot' &&
              `${pendingPoints.length}/${CURVE_POINTS_REQUIRED.nthRoot} points. ${pendingPoints.length === 0 ? `${curveInputMode === 'type' ? 'Type' : 'Click'} the start point first` : `${curveInputMode === 'type' ? 'Type' : 'Click'} one more point`}`}
            {mode === 'exponential' &&
              `${pendingPoints.length}/${CURVE_POINTS_REQUIRED.exponential} points. ${
                pendingPoints.length === 0 ? `${curveInputMode === 'type' ? 'Type' : 'Click'} 1st point` :
                pendingPoints.length === 1 ? `${curveInputMode === 'type' ? 'Type' : 'Click'} 2nd point (same x-spacing)` :
                `${curveInputMode === 'type' ? 'Type' : 'Click'} 3rd point (same x-spacing)`
              }`}
            {mode === 'logarithmic' &&
              `${pendingPoints.length}/${CURVE_POINTS_REQUIRED.logarithmic} points. ${
                pendingPoints.length === 0
                  ? `${curveInputMode === 'type' ? 'Type' : 'Click'} the anchor point (where log = 0)`
                  : `${curveInputMode === 'type' ? 'Type' : 'Click'} one more point`
              }`}
            {mode === 'sine' &&
              `${pendingPoints.length}/${CURVE_POINTS_REQUIRED.sine} points. ${pendingPoints.length === 0 ? `${curveInputMode === 'type' ? 'Type' : 'Click'} max point` : pendingPoints.length === 1 ? `${curveInputMode === 'type' ? 'Type' : 'Click'} min point` : `${curveInputMode === 'type' ? 'Type' : 'Click'} zero crossing`}`}
            {mode === 'cosine' &&
              `${pendingPoints.length}/${CURVE_POINTS_REQUIRED.cosine} points. ${pendingPoints.length === 0 ? `${curveInputMode === 'type' ? 'Type' : 'Click'} max point` : pendingPoints.length === 1 ? `${curveInputMode === 'type' ? 'Type' : 'Click'} min point` : `${curveInputMode === 'type' ? 'Type' : 'Click'} zero crossing`}`}
            {mode === 'tangent' &&
              `${pendingPoints.length}/${CURVE_POINTS_REQUIRED.tangent} points. ${pendingPoints.length === 0 ? `${curveInputMode === 'type' ? 'Type' : 'Click'} midline crossing` : pendingPoints.length === 1 ? `${curveInputMode === 'type' ? 'Type' : 'Click'} adjacent asymptote x` : `${curveInputMode === 'type' ? 'Type' : 'Click'} one more point on the curve`}`}
            {mode.startsWith('polynomial') && (() => {
              const degree = parseInt(mode.replace('polynomial', ''), 10);
              const required = getPolynomialPointsRequired(degree);
              const remaining = required - pendingPoints.length;
              return `${pendingPoints.length}/${required} points. ${curveInputMode === 'type' ? 'Type' : 'Click'} ${remaining} more to define the polynomial`;
            })()}
          </span>
        ) : (
          <>
            {mode === 'point' && (pointInputMode === 'type'
              ? 'Enter coordinates below to add a point'
              : 'Click on the grid to place a point')}
            {mode === 'line' && (lineInputMode === 'equation'
              ? 'Enter an equation below to add a line'
              : 'Click to set the first point of a line')}
            {mode === 'polygon' && 'Click to add polygon vertices (minimum 3 points)'}
            {mode === 'circle' && 'Click to set the center of a circle'}
            {mode === 'quadratic' && (curveInputMode === 'type'
              ? `Type ${CURVE_POINTS_REQUIRED.quadratic} coordinates to define a parabola (y = ax² + bx + c)`
              : `Click ${CURVE_POINTS_REQUIRED.quadratic} points to define a parabola (y = ax² + bx + c)`)}
            {mode === 'absolute' && (curveInputMode === 'type'
              ? 'Type the vertex, then one more coordinate to define absolute value (y = a|x - h| + k)'
              : 'Click the vertex, then one more point to define absolute value (y = a|x - h| + k)')}
            {mode === 'sqrt' && (curveInputMode === 'type'
              ? 'Type the start coordinate, then one more to define square root (y = a√(x - h) + k)'
              : 'Click the start point, then one more to define square root (y = a√(x - h) + k)')}
            {mode === 'nthRoot' && (curveInputMode === 'type'
              ? 'Type the start coordinate, then one more to define nth root (y = a·ⁿ√(x - h) + k)'
              : 'Click the start point, then one more to define nth root (y = a·ⁿ√(x - h) + k)')}
            {mode === 'exponential' && (curveInputMode === 'type'
              ? 'Type 3 coordinates with equal x-spacing to define exponential (y = a\u00B7b\u02E3 + c)'
              : 'Click 3 points with equal x-spacing to define exponential (y = a\u00B7b\u02E3 + c)')}
            {mode === 'logarithmic' && (() => {
              const baseLabel = Math.abs(logBase - Math.E) < 1e-10 ? 'ln' : `log_${Number.isInteger(logBase) ? logBase : logBase.toFixed(2)}`;
              return curveInputMode === 'type'
                ? `Type 2 coordinates to define logarithmic curve (y = ${baseLabel}(x - h) + k)`
                : `Click 2 points to define logarithmic curve (y = ${baseLabel}(x - h) + k)`;
            })()}
            {mode === 'sine' && (curveInputMode === 'type'
              ? 'Type max, min, then zero crossing coordinates to define sine wave'
              : 'Click max point, min point, then a zero crossing to define sine wave')}
            {mode === 'cosine' && (curveInputMode === 'type'
              ? 'Type max, min, then zero crossing coordinates to define cosine wave'
              : 'Click max point, min point, then a zero crossing to define cosine wave')}
            {mode === 'tangent' && (curveInputMode === 'type'
              ? 'Type a midline crossing, then an adjacent asymptote x, then one more point to define tangent (y = a·tan(bx + c) + d)'
              : 'Click a midline crossing, then a neighboring asymptote, then one more point on the curve to define tangent (y = a·tan(bx + c) + d)')}
            {mode.startsWith('polynomial') && (() => {
              const degree = parseInt(mode.replace('polynomial', ''), 10);
              const required = getPolynomialPointsRequired(degree);
              return curveInputMode === 'type'
                ? `Type ${required} coordinates to define a degree-${degree} polynomial`
                : `Click ${required} points to define a degree-${degree} polynomial`;
            })()}
          </>
        )}
      </Typography>

      {/* Controls */}
      <GridControls
        mode={mode}
        setMode={setMode}
        allowedModes={allowedModes}
        onUndo={undo}
        onClear={clear}
        onCancel={cancelOperation}
        canUndo={canUndo}
        hasElements={elements.length > 0}
        hasPending={pendingPoints.length > 0}
        compact={true}
        lineInputMode={lineInputMode}
        setLineInputMode={setLineInputMode}
        pointInputMode={pointInputMode}
        setPointInputMode={setPointInputMode}
        allowedPointInputMethods={allowedPointInputMethods}
        allowedLineInputMethods={allowedLineInputMethods}
        curveInputMode={curveInputMode}
        setCurveInputMode={setCurveInputMode}
        allowedCurveInputMethods={allowedCurveInputMethods}
        logBase={logBase}
        setLogBase={setLogBase}
        customLogBase={customLogBase}
        setCustomLogBase={setCustomLogBase}
      />

      {/* Equation input for line mode */}
      {mode === 'line' && lineInputMode === 'equation' && (
        <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            label="Enter equation"
            value={equationInput}
            onChange={(e) => {
              setEquationInput(e.target.value);
              setEquationError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddEquationLine();
              }
            }}
            placeholder="y = 2x + 1"
            error={!!equationError}
            helperText={equationError || 'Formats: y=mx+b, ax+by=c, x=c, y=c'}
            size="small"
            sx={{ width: 200 }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddEquationLine}
            size="small"
            sx={{ mt: 0.5 }}
          >
            Add Line
          </Button>
        </Box>
      )}

      {/* Coordinate input for point mode */}
      {mode === 'point' && pointInputMode === 'type' && (
        <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            label="Enter coordinates"
            value={coordinateInput}
            onChange={(e) => {
              setCoordinateInput(e.target.value);
              setCoordinateError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddTypedPoint();
              }
            }}
            placeholder="(1/2, 3/4) or (0.5, -1)"
            error={!!coordinateError}
            helperText={coordinateError || 'Supports fractions: (1/2, 3/4)'}
            size="small"
            sx={{ width: 220 }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddTypedPoint}
            size="small"
            sx={{ mt: 0.5 }}
          >
            Add Point
          </Button>
        </Box>
      )}

      {/* Curve coordinate input for curve modes */}
      {isCurveMode(mode) && curveInputMode === 'type' && (
        <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            label="Enter coordinates"
            value={curveCoordinateInput}
            onChange={(e) => {
              setCurveCoordinateInput(e.target.value);
              setCurveCoordinateError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddTypedCurvePoint();
              }
            }}
            placeholder="(1/2, 3/4) or (0.5, -1)"
            error={!!curveCoordinateError}
            helperText={curveCoordinateError || `Point ${pendingPoints.length + 1}/${getCurvePointsRequired(mode)} — Supports fractions`}
            size="small"
            sx={{ width: 220 }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddTypedCurvePoint}
            size="small"
            sx={{ mt: 0.5 }}
          >
            Add
          </Button>
        </Box>
      )}

      {/* Grid */}
      <CoordinateGrid
        config={gridConfig}
        elements={elements}
        pendingPoints={pendingPoints}
        mode={mode}
        readOnly={false}
        onGridClick={handleGridClick}
        width={400}
        height={400}
      />

      {/* Answer Summary */}
      <Box sx={{ mt: 1.5 }}>
        <Typography variant="caption" sx={{ color: colors.grey[400], display: 'block', mb: 0.5 }}>
          Your answer ({elements.length} element{elements.length !== 1 ? 's' : ''}):
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {elements.length === 0 ? (
            <Typography variant="caption" sx={{ color: colors.grey[500], fontStyle: 'italic' }}>
              No elements added yet
            </Typography>
          ) : (
            elements.map((el, i) => (
              <Chip
                key={el.id || i}
                label={el.type === 'curve' && el.clickedPoints?.length
                  ? `${(el.curveType || 'Curve').charAt(0).toUpperCase() + (el.curveType || 'Curve').slice(1)}: ${el.clickedPoints.map(p => formatCoordinate(p.x, p.y)).join(', ')}`
                  : formatElementSummary(el)}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: colors.blueAccent[500],
                  color: colors.grey[200],
                  fontSize: '0.7rem',
                }}
              />
            ))
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CoordinateGridStudent;
